import type {ChangeEvent, Dispatch, RefObject, SetStateAction} from 'react';
import {useCallback, useState} from 'react';

import {checkIfLabelExists, createIssue, createLabel} from '../../utils/githubHelpers';

interface useNewIssueProps {
    forwardedRef: RefObject<HTMLDialogElement | null>;
    setUpdated: Dispatch<SetStateAction<number>>;
    storyId: string | null;
}

/**
 * Use new issue form.
 *
 * @param props              The props.
 * @param props.forwardedRef The forwarded ref.
 * @param props.storyId      The story ID.
 * @param props.setUpdated   The set updated.
 * @returns The new issue form handler.
 */
export const useNewIssueForm = ({forwardedRef, setUpdated, storyId}: useNewIssueProps) => {
    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    /**
     * Handle title.
     *
     * @param event The event.
     */
    const handleTitle = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setTitle(event.target.value);
    }, [setTitle]);

    /**
     * Handle body.
     *
     * @param event The event.
     */
    const handleBody = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
        setBody(event.target.value);
    }, [setBody]);

    const handleNewIssue = useCallback(async () => {
        // eslint-disable-next-line @nfq/no-magic-numbers
        let label = 201;

        if (!(await checkIfLabelExists(storyId!))) {
            label = await createLabel(storyId!);
        }

        // eslint-disable-next-line @nfq/no-magic-numbers
        if (label < 400) {
            const issue = await createIssue(title, body, window.location.href, storyId!);

            if (issue !== null) {
                setTitle('');
                setBody('');
                setUpdated(Date.now());
                forwardedRef.current?.close();

                return;
            }
        }

        setError('Something went wrong. Please try again later.');
    }, [forwardedRef, body, title, setUpdated, storyId]);

    return {
        body,
        error,
        handleBody,
        handleNewIssue,
        handleTitle,
        title
    };
};